import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { featureService } from '@common/api/featureService';
import { Check, X } from 'lucide-react';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './ContactSalesModal.module.scss';

interface ContactSalesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  jobTitle: string;
  companySize: string;
  brandCount: string;
  primaryUseCase: string;
  needs: string;
}

const ContactSalesModal: React.FC<ContactSalesModalProps> = ({ isOpen, onClose }) => {
    const { showToast } = useToast();
    const { userBilling } = useAuth();
    const [formData, setFormData] = useState<FormData>({
        firstName: '',
        lastName: '',
        email: '',
        companyName: '',
        jobTitle: '',
        companySize: '',
        brandCount: '',
        primaryUseCase: '',
        needs: ''
    });

    const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
    const [isSubmitted, setIsSubmitted] = useState(false);

    if (!isOpen) return null;

    const validateForm = () => {
        const newErrors: Partial<Record<keyof FormData, string>> = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (!formData.companyName.trim()) {
            newErrors.companyName = 'Company name is required';
        }

        if (!formData.companySize) {
            newErrors.companySize = 'Please select company size';
        }

        if (!formData.brandCount) {
            newErrors.brandCount = 'Please select number of brands/stores';
        }

        if (!formData.primaryUseCase) {
            newErrors.primaryUseCase = 'Please select a primary use case';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
    
        if (validateForm()) {
            try {
                await featureService.sendGenericMessage({
                    title: "Contact Enterprise Sales",
                    content: {
                        "First Name": formData.firstName,
                        "Last Name": formData.lastName,
                        "Work Email": formData.email,
                        "Company Name": formData.companyName,
                        "Job Title": formData.jobTitle || 'N/A',
                        "Company Size": formData.companySize,
                        "Number of Brands/Stores": formData.brandCount,
                        "Current Plan": userBilling?.planText?.planDisplayText || 'N/A',
                        "Primary Use Case": formData.primaryUseCase,
                        "Needs": formData.needs || 'N/A'
                    }
                });
                setIsSubmitted(true);
                setErrors({});
            } catch (error) {
                console.error("Failed to send contact sales request", error);
                showToast("Failed to send request. Please try again later.", "error");
            }
        }
    };

    const handleClose = () => {
        onClose();
        // Reset state after a short delay
        setTimeout(() => {
            setIsSubmitted(false);
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                companyName: '',
                jobTitle: '',
                companySize: '',
                brandCount: '',
                primaryUseCase: '',
                needs: ''
            });
        }, 200);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user types
        if (errors[name as keyof FormData]) {
            setErrors(prev => ({
                ...prev,
                [name]: undefined
            }));
        }
    };

    const modalContent = (
        <div className={styles.overlay} onClick={handleClose}>
            <div className={styles.container}>
                <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                    {/* Close Button */}
                    <button 
                        onClick={handleClose}
                        className={styles.closeButton}
                    >
                        <X size={24} />
                    </button>

                    <div className={styles.content}>
                        {isSubmitted ? (
                            <div className={styles.successState}>
                                <div className={styles.successIcon}>
                                    <Check size={40} className={styles.checkIcon} strokeWidth={3} />
                                </div>
                                <h2 className={styles.successTitle}>Thank you!</h2>
                                <p className={styles.successSubtitle}>Your request has been sent.</p>
                                <p className={styles.successMessage}>
                                    Our team will review your information and get back to you within 24 hours to schedule a personalized demo.
                                </p>
                                <button
                                    onClick={handleClose}
                                    className={styles.closeSuccessButton}
                                >
                                    Close
                                </button>
                            </div>
                        ) : (
                            <>
                                <h2 className={styles.title}>Contact Enterprise Sales</h2>
                                <p className={styles.subtitle}>Tell us about your business needs and we'll create a custom solution for you.</p>

                                <form onSubmit={handleSubmit} className={styles.form} noValidate>
                                    <div className={styles.grid}>
                                        {/* First Name */}
                                        <div className={styles.field}>
                                            <label className={styles.label}>
                                                First Name
                                                <span className={styles.required}>*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="firstName"
                                                value={formData.firstName}
                                                onChange={handleChange}
                                                placeholder="First name"
                                                className={`${styles.input} ${errors.firstName ? styles.errorInput : ''}`}
                                            />
                                            {errors.firstName ? <p className={styles.errorMessage}>{errors.firstName}</p> : null}
                                        </div>

                                        {/* Last Name */}
                                        <div className={styles.field}>
                                            <label className={styles.label}>
                                                Last Name
                                                <span className={styles.required}>*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="lastName"
                                                value={formData.lastName}
                                                onChange={handleChange}
                                                placeholder="Last name"
                                                className={`${styles.input} ${errors.lastName ? styles.errorInput : ''}`}
                                            />
                                            {errors.lastName ? <p className={styles.errorMessage}>{errors.lastName}</p> : null}
                                        </div>

                                        {/* Work Email */}
                                        <div className={styles.field}>
                                            <label className={styles.label}>
                                                Work Email
                                                <span className={styles.required}>*</span>
                                            </label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                placeholder="you@company.com"
                                                className={`${styles.input} ${errors.email ? styles.errorInput : ''}`}
                                            />
                                            {errors.email ? <p className={styles.errorMessage}>{errors.email}</p> : null}
                                        </div>

                                        {/* Company Name */}
                                        <div className={styles.field}>
                                            <label className={styles.label}>
                                                Company Name
                                                <span className={styles.required}>*</span>
                                            </label>
                                            <input 
                                                type="text" 
                                                name="companyName"
                                                value={formData.companyName}
                                                onChange={handleChange}
                                                placeholder="Your company"
                                                className={`${styles.input} ${errors.companyName ? styles.errorInput : ''}`}
                                            />
                                            {errors.companyName ? <p className={styles.errorMessage}>{errors.companyName}</p> : null}
                                        </div>

                                        {/* Job Title */}
                                        <div className={styles.field}>
                                            <label className={styles.label}>Job Title</label>
                                            <input 
                                                type="text" 
                                                name="jobTitle"
                                                value={formData.jobTitle}
                                                onChange={handleChange}
                                                placeholder="e.g. CEO, Marketing Director"
                                                className={styles.input}
                                            />
                                        </div>

                                        {/* Company Size */}
                                        <div className={styles.field}>
                                            <label className={styles.label}>
                                                Company Size
                                                <span className={styles.required}>*</span>
                                            </label>
                                            <select 
                                                name="companySize"
                                                value={formData.companySize}
                                                onChange={handleChange}
                                                className={`${styles.select} ${errors.companySize ? styles.errorInput : ''}`}
                                            >
                                                <option value="">Select size</option>
                                                <option value="1-10">1-10 employees</option>
                                                <option value="11-50">11-50 employees</option>
                                                <option value="51-200">51-200 employees</option>
                                                <option value="201-500">201-500 employees</option>
                                                <option value="501+">501+ employees</option>
                                            </select>
                                            {errors.companySize ? <p className={styles.errorMessage}>{errors.companySize}</p> : null}
                                        </div>

                                        {/* Brand Count */}
                                        <div className={styles.field}>
                                            <label className={styles.label}>
                                                Number of Brands/Stores
                                                <span className={styles.required}>*</span>
                                            </label>
                                            <select 
                                                name="brandCount"
                                                value={formData.brandCount}
                                                onChange={handleChange}
                                                className={`${styles.select} ${errors.brandCount ? styles.errorInput : ''}`}
                                            >
                                                <option value="">Select count</option>
                                                <option value="1">1</option>
                                                <option value="2-5">2-5</option>
                                                <option value="6-10">6-10</option>
                                                <option value="11+">11+</option>
                                            </select>
                                            {errors.brandCount ? <p className={styles.errorMessage}>{errors.brandCount}</p> : null}
                                        </div>

                                        {/* Primary Use Case */}
                                        <div className={`${styles.field} ${styles.fullWidth}`}>
                                            <label className={styles.label}>
                                                Primary Use Case
                                                <span className={styles.required}>*</span>
                                            </label>
                                            <select 
                                                name="primaryUseCase"
                                                value={formData.primaryUseCase}
                                                onChange={handleChange}
                                                className={`${styles.select} ${errors.primaryUseCase ? styles.errorInput : ''}`}
                                            >
                                                <option value="">Select use case</option>
                                                <option value="market-analysis">Market Analysis & Insights</option>
                                                <option value="content-creation">Content Creation</option>
                                                <option value="product-research">Product Research</option>
                                                <option value="competitor-tracking">Competitor Tracking</option>
                                                <option value="other">Other</option>
                                            </select>
                                            {errors.primaryUseCase ? <p className={styles.errorMessage}>{errors.primaryUseCase}</p> : null}
                                        </div>

                                        {/* Needs */}
                                        <div className={`${styles.field} ${styles.fullWidth}`} style={{ gridColumn: '1 / -1' }}>
                                            <label className={styles.label}>Anything else we should know?</label>
                                            <textarea 
                                                name="needs"
                                                value={formData.needs}
                                                onChange={handleChange}
                                                placeholder="Tell us about your specific needs..."
                                                rows={3}
                                                className={styles.textarea}
                                            />
                                        </div>
                                    </div>

                                    <button 
                                        type="submit" 
                                        className={styles.submitButton}
                                    >
                                        Submit Request
                                    </button>
                  
                                    <p className={styles.privacyText}>
                                        By submitting this form, you agree to our Terms of Service and Privacy Policy.
                                    </p>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default ContactSalesModal;
